import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { isAxiosError } from 'axios';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  MenuItem,
  Paper,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import EditNoteIcon from '@mui/icons-material/EditNoteOutlined';
import CheckCircleIcon from '@mui/icons-material/CheckCircleOutlined';
import CancelIcon from '@mui/icons-material/CancelOutlined';
import { decideChangeRequest, listChangeRequests } from '../../api/students';
import { ChangeRequestStatus, FIELD_LABELS } from '../../types/portal';
import { PageHeader } from '../../components/PageHeader';
import { RejectChangeRequestDialog } from './RejectChangeRequestDialog';

const extractErrorMessage = (err: unknown): string =>
  isAxiosError(err) ? (err.response?.data?.message ?? 'Something went wrong.') : 'Something went wrong.';

/** Counselor review area (Phase 67) — "Add a Student Change Requests area
 * for Counselors": lists change requests (default PENDING), clearly shows
 * OLD VALUE -> NEW VALUE, and lets the Counselor Approve or Reject
 * (rejection requires a reason). Gated by the existing `students:update`
 * permission (reused, not a new module) — every role that can already
 * edit a student's record can also review a change to it. */
export const StudentChangeRequestsPage = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [statusFilter, setStatusFilter] = useState<ChangeRequestStatus>('PENDING');
  const [rejectTargetId, setRejectTargetId] = useState<number | null>(null);
  const [decisionError, setDecisionError] = useState<string | null>(null);

  const requestsQuery = useQuery({
    queryKey: ['change-requests', statusFilter],
    queryFn: () => listChangeRequests(statusFilter),
  });

  const invalidate = () => queryClient.invalidateQueries({ queryKey: ['change-requests'] });

  const decideMutation = useMutation({
    mutationFn: ({ id, status, reason }: { id: number; status: 'APPROVED' | 'REJECTED'; reason?: string }) =>
      decideChangeRequest(id, status, reason),
    onSuccess: () => {
      setDecisionError(null);
      setRejectTargetId(null);
      invalidate();
    },
    onError: (err) => setDecisionError(extractErrorMessage(err)),
  });

  return (
    <Box sx={{ p: 3 }}>
      <PageHeader
        icon={EditNoteIcon}
        color="#B8860B"
        title="Student Change Requests"
        subtitle="Review student-submitted edits to their own record before they take effect."
      />

      <TextField
        select
        size="small"
        label="Status"
        sx={{ width: 200, mb: 2 }}
        value={statusFilter}
        onChange={(e) => setStatusFilter(e.target.value as ChangeRequestStatus)}
      >
        <MenuItem value="PENDING">Pending</MenuItem>
        <MenuItem value="APPROVED">Approved</MenuItem>
        <MenuItem value="REJECTED">Rejected</MenuItem>
      </TextField>

      {decisionError && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setDecisionError(null)}>
          {decisionError}
        </Alert>
      )}

      {requestsQuery.isLoading && <CircularProgress size={24} />}

      <Stack spacing={1.5}>
        {requestsQuery.data?.map((request) => (
          <Paper key={request.id} variant="outlined" sx={{ p: 2 }}>
            <Stack direction="row" justifyContent="space-between" alignItems="flex-start" flexWrap="wrap" gap={1}>
              <Box>
                <Typography
                  variant="subtitle2"
                  sx={{ cursor: 'pointer' }}
                  onClick={() => navigate(`/students/${request.student.id}`)}
                >
                  {request.student.firstName} {request.student.lastName} ({request.student.studentNumber})
                </Typography>
                <Typography variant="body2" sx={{ mt: 0.5 }}>
                  {FIELD_LABELS[request.fieldName]}: <strong>{request.oldValue || '—'}</strong> →{' '}
                  <strong>{request.newValue}</strong>
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Requested {new Date(request.requestedAt).toLocaleString()}
                </Typography>
                {request.status === 'REJECTED' && request.decisionReason && (
                  <Typography variant="body2" color="text.secondary">
                    Reason: {request.decisionReason}
                  </Typography>
                )}
              </Box>
              {request.status === 'PENDING' ? (
                <Stack direction="row" spacing={1}>
                  <Button
                    size="small"
                    variant="outlined"
                    color="success"
                    startIcon={<CheckCircleIcon />}
                    disabled={decideMutation.isPending}
                    onClick={() => decideMutation.mutate({ id: request.id, status: 'APPROVED' })}
                  >
                    Approve
                  </Button>
                  <Button
                    size="small"
                    variant="outlined"
                    color="error"
                    startIcon={<CancelIcon />}
                    disabled={decideMutation.isPending}
                    onClick={() => {
                      setDecisionError(null);
                      setRejectTargetId(request.id);
                    }}
                  >
                    Reject
                  </Button>
                </Stack>
              ) : (
                <Chip
                  size="small"
                  label={request.status}
                  color={request.status === 'APPROVED' ? 'success' : 'error'}
                />
              )}
            </Stack>
          </Paper>
        ))}
        {requestsQuery.data?.length === 0 && (
          <Typography variant="body2" color="text.secondary">
            No {statusFilter.toLowerCase()} change requests.
          </Typography>
        )}
      </Stack>

      <RejectChangeRequestDialog
        open={rejectTargetId !== null}
        errorMessage={decisionError}
        submitting={decideMutation.isPending}
        onClose={() => setRejectTargetId(null)}
        onSubmit={(reason) => decideMutation.mutate({ id: rejectTargetId!, status: 'REJECTED', reason })}
      />
    </Box>
  );
};
